import { useState, useCallback, useEffect } from 'react';

// Lazy import to avoid blocking initialization
let backgroundAIProcessor = null;
const getBackgroundProcessor = async () => {
  if (!backgroundAIProcessor) {
    const module = await import('../services/backgroundAIProcessor');
    backgroundAIProcessor = module.default;
  }
  return backgroundAIProcessor;
};

const STORAGE_KEY = 'ai_concierge_conversation';
const SESSION_KEY = 'ai_concierge_session';
const PREFERENCES_KEY = 'ai_concierge_preferences';
const PROCESSING_KEY = 'ai_concierge_processing';
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const usePersistentAIConversation = (preferences = {}, tier = 'free') => {
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [persistedPreferences, setPersistedPreferences] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  // AI service is now handled by backgroundAIProcessor
  // const aiService = useMemo(() => new AIService(tier), [tier]);

  // Initialize conversation from localStorage
  useEffect(() => {
    const initializeConversation = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const sessionData = localStorage.getItem(SESSION_KEY);
        const preferencesData = localStorage.getItem(PREFERENCES_KEY);
        const processingData = localStorage.getItem(PROCESSING_KEY);
        
        if (stored && sessionData) {
          const { conversation: storedConversation, sessionId: storedSessionId } = JSON.parse(stored);
          const { sessionId: currentSessionId, timestamp: sessionTimestamp } = JSON.parse(sessionData);
          
          // Check if session is still valid (within 24 hours)
          const now = Date.now();
          const sessionAge = now - sessionTimestamp;
          
          if (sessionAge < SESSION_DURATION && storedSessionId === currentSessionId) {
            // Restore conversation with proper date objects
            const restoredConversation = storedConversation.map(msg => ({
              ...msg,
              timestamp: new Date(msg.timestamp)
            }));
            console.log('🔄 Restoring conversation from localStorage:', restoredConversation.length, 'messages');
            console.log('🔄 Session valid, age:', sessionAge, 'ms');
            setConversation(restoredConversation);
            setSessionId(currentSessionId);
            
            // Restore preferences if available
            if (preferencesData) {
              const storedPreferences = JSON.parse(preferencesData);
              setPersistedPreferences(storedPreferences);
            }
            
            // Restore processing state if available
            if (processingData) {
              const { isProcessing: wasProcessing, startTime } = JSON.parse(processingData);
              const processingAge = now - startTime;
              
              // If processing started less than 5 minutes ago, restore processing state
              if (wasProcessing && processingAge < 5 * 60 * 1000) {
                setIsProcessing(true);
                setIsLoading(true);
              }
            }
            
            return;
          }
        }
        
        // If no valid session or expired, start fresh
        const newSessionId = Date.now().toString();
        const initialMessage = {
          id: 1,
          type: 'ai',
          message: "Hi! I'm your AI Trip Concierge. I can help you plan the perfect trip! What would you like to explore today?",
          timestamp: new Date(),
        };
        
        setConversation([initialMessage]);
        setSessionId(newSessionId);
        setError(null);
        
        // Update localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          conversation: [initialMessage],
          timestamp: Date.now(),
          sessionId: newSessionId
        }));
        localStorage.setItem(SESSION_KEY, JSON.stringify({
          sessionId: newSessionId,
          timestamp: Date.now()
        }));
      } catch (error) {
        console.error('Error loading conversation from localStorage:', error);
        // Start fresh on error
        const newSessionId = Date.now().toString();
        const initialMessage = {
          id: 1,
          type: 'ai',
          message: "Hi! I'm your AI Trip Concierge. I can help you plan the perfect trip! What would you like to explore today?",
          timestamp: new Date(),
        };
        
        setConversation([initialMessage]);
        setSessionId(newSessionId);
        setError(null);
        
        // Update localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          conversation: [initialMessage],
          timestamp: Date.now(),
          sessionId: newSessionId
        }));
        localStorage.setItem(SESSION_KEY, JSON.stringify({
          sessionId: newSessionId,
          timestamp: Date.now()
        }));
      }
    };

    initializeConversation();
  }, []);

  // Clear conversation and session
  const clearConversation = useCallback(() => {
    console.log('🗑️ clearConversation called - clearing all conversation data');
    const newSessionId = Date.now().toString();
    const initialMessage = {
      id: 1,
      type: 'ai',
      message: "Hi! I'm your AI Trip Concierge. I can help you plan the perfect trip! What would you like to explore today?",
      timestamp: new Date(),
    };
    
    setConversation([initialMessage]);
    setSessionId(newSessionId);
    setError(null);
    
    // Update localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      conversation: [initialMessage],
      timestamp: Date.now(),
      sessionId: newSessionId
    }));
    localStorage.setItem(SESSION_KEY, JSON.stringify({
      sessionId: newSessionId,
      timestamp: Date.now()
    }));
  }, []);

  // Save conversation to localStorage
  const saveConversation = useCallback((newConversation) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        conversation: newConversation,
        timestamp: Date.now(),
        sessionId: sessionId
      }));
    } catch (error) {
      console.error('Error saving conversation to localStorage:', error);
    }
  }, [sessionId]);

  // Save preferences to localStorage (event-based, no state update)
  const savePreferences = useCallback((newPreferences) => {
    try {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(newPreferences));
      // Don't update persistedPreferences state to prevent infinite loops
      // The state will be updated only when actually needed
    } catch (error) {
      console.error('Error saving preferences to localStorage:', error);
    }
  }, []);

  // Save processing state to localStorage
  const saveProcessingState = useCallback((processing, startTime = null) => {
    try {
      localStorage.setItem(PROCESSING_KEY, JSON.stringify({
        isProcessing: processing,
        startTime: startTime || Date.now()
      }));
      setIsProcessing(processing);
    } catch (error) {
      console.error('Error saving processing state to localStorage:', error);
    }
  }, []);

  // Update conversation and save to localStorage
  const updateConversation = useCallback((newConversation) => {
    setConversation(newConversation);
    saveConversation(newConversation);
  }, [saveConversation]);

  // Send message to AI and get response - NON-BLOCKING
  const sendMessage = useCallback(async (message) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: message.trim(),
      timestamp: new Date(),
    };

    // Add user message to conversation
    const newConversation = [...conversation, userMessage];
    updateConversation(newConversation);
    setIsLoading(true);
    setError(null);

    try {
      // FREE TIER: Use direct API calls for real-time responses
      if (tier === 'free') {
        console.log('💬 Free tier: Using direct API call for real-time response');
        
        // Lazy import AIService
        const AIService = (await import('../services/aiService')).default;
        const aiService = new AIService(tier);
        
        // Prepare conversation history
        const conversationHistory = newConversation.slice(-10).map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.message
        }));
        
        // Get AI response (blocking call for free tier)
        const aiResponse = await aiService.generateResponse(
          message.trim(),
          preferences,
          conversationHistory
        );
        
        // Add AI response to conversation
        const aiMessage = {
          id: Date.now() + 1,
          type: 'ai',
          message: aiResponse,
          timestamp: new Date(),
        };

        const finalConversation = [...newConversation, aiMessage];
        updateConversation(finalConversation);
        setIsLoading(false);
        
      } else {
        // PREMIUM TIER: Use background processing (non-blocking)
        const jobId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Get background processor (lazy import)
        const processor = await getBackgroundProcessor();
        
        // Prepare conversation history for AI
        const conversationHistory = newConversation.slice(-10).map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.message
        }));
        
        // Pass user message and conversation history to background processor
        const enhancedPreferences = {
          ...preferences,
          userMessage: message.trim(),
          conversationHistory: conversationHistory,
          isStructuredRequest: false
        };
        
        // Start background processing for chat (non-blocking)
        processor.startJob(jobId, enhancedPreferences, tier);
        
        // Add listener for job completion
        const handleJobUpdate = (updatedJob) => {
          console.log(`📊 Premium tier job update: ${updatedJob.status}`, updatedJob);
          
          if (updatedJob.status === 'completed') {
            console.log('✅ Premium tier job completed - adding response to conversation');
            // Add AI response to conversation
            const aiMessage = {
              id: Date.now() + 1,
              type: 'ai',
              message: updatedJob.result.message,
              timestamp: new Date(),
            };

            const finalConversation = [...newConversation, aiMessage];
            updateConversation(finalConversation);
            
            console.log('🔓 Premium tier: Setting isLoading = false');
            setIsLoading(false);
            
            // Remove listener
            processor.removeListener(jobId, handleJobUpdate);
            
          } else if (updatedJob.status === 'failed') {
            console.log('❌ Premium tier job failed');
            // Add error message to conversation
            const errorMessage = {
              id: Date.now() + 1,
              type: 'ai',
              message: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
              timestamp: new Date(),
            };

            const errorConversation = [...newConversation, errorMessage];
            updateConversation(errorConversation);
            
            console.log('🔓 Premium tier: Setting isLoading = false (error)');
            setIsLoading(false);
            setError(updatedJob.error);
            
            // Remove listener
            processor.removeListener(jobId, handleJobUpdate);
          }
        };

        processor.addListener(jobId, handleJobUpdate);
        
        console.log(`💬 Premium tier: Background chat job started: ${jobId} - Navigation is now free!`);
        
        // Note: isLoading stays true until job completes (handled in handleJobUpdate)
      }
      
    } catch (err) {
      console.error('Failed to send AI message:', err);
      
      // Add error message to conversation
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        message: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date(),
        isError: true
      };
      
      const errorConversation = [...newConversation, errorMessage];
      updateConversation(errorConversation);
      
      setError(err.message || 'Failed to get AI response');
      setIsLoading(false);
    }
  }, [conversation, preferences, tier, updateConversation]);

  // Start AI processing (for recommendations generation) - NON-BLOCKING
  const startAIProcessing = useCallback(async (processingMessage = "Generating AI recommendations...") => {
    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: processingMessage,
      timestamp: new Date(),
    };

    // Add user message to conversation
    const newConversation = [...conversation, userMessage];
    updateConversation(newConversation);
    
    // Set processing state (non-blocking)
    setIsProcessing(true);
    saveProcessingState(true);
    setError(null);

    // Start background job
    const jobId = `ai_job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      // Get background processor (lazy import)
      const processor = await getBackgroundProcessor();
      
      // Start background processing (non-blocking)
      processor.startJob(jobId, preferences, tier);
      
      // Add listener for job completion
      const handleJobUpdate = (updatedJob) => {
        if (updatedJob.status === 'completed') {
          // Add AI response to conversation
          const aiMessage = {
            id: Date.now() + 1,
            type: 'ai',
            message: updatedJob.result.message,
            timestamp: new Date(),
            data: updatedJob.result.data // Include structured data
          };

          const finalConversation = [...conversation, userMessage, aiMessage];
          updateConversation(finalConversation);
          
          setIsProcessing(false);
          saveProcessingState(false);
          
          // Remove listener
          processor.removeListener(jobId, handleJobUpdate);
          
        } else if (updatedJob.status === 'failed') {
          // Add error message to conversation
          const errorMessage = {
            id: Date.now() + 1,
            type: 'ai',
            message: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
            timestamp: new Date(),
          };

          const errorConversation = [...conversation, userMessage, errorMessage];
          updateConversation(errorConversation);
          
          setIsProcessing(false);
          saveProcessingState(false);
          setError(updatedJob.error);
          
          // Remove listener
          processor.removeListener(jobId, handleJobUpdate);
        }
      };

      processor.addListener(jobId, handleJobUpdate);
      
      console.log(`🚀 Background AI job started: ${jobId} - Navigation is now free!`);
      
    } catch (err) {
      console.error('Failed to start background job:', err);
      setError(err.message || 'Failed to start AI processing');
      setIsProcessing(false);
      saveProcessingState(false);
    }
  }, [conversation, preferences, tier, updateConversation, saveProcessingState]);

  // Update preferences (this will trigger a new session)
  const updatePreferences = useCallback((newPreferences) => {
    console.log('🔄 updatePreferences called, clearing conversation');
    console.log('🔄 New preferences:', newPreferences);
    
    // Only clear conversation if preferences have actually changed significantly
    // Check if this is just a restoration of existing preferences
    const hasSignificantChanges = newPreferences.destinations || 
                                 newPreferences.startDate || 
                                 newPreferences.endDate;
    
    if (hasSignificantChanges) {
      console.log('🔄 Significant preference changes detected, clearing conversation');
      clearConversation();
    } else {
      console.log('🔄 No significant changes, keeping conversation');
    }
  }, [clearConversation]);

  // Check if session is expired
  const isSessionExpired = useCallback(() => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) return true;
      
      const { timestamp } = JSON.parse(sessionData);
      const now = Date.now();
      const sessionAge = now - timestamp;
      
      return sessionAge >= SESSION_DURATION;
    } catch (error) {
      console.error('Error checking session expiration:', error);
      return true;
    }
  }, []);

  // Check session on component mount
  useEffect(() => {
    console.log('🔍 Checking session expiration on mount...');
    const expired = isSessionExpired();
    console.log('🔍 Session expired?', expired);
    
    if (expired) {
      console.log('🗑️ Session expired, clearing conversation');
      clearConversation();
    } else {
      console.log('✅ Session valid, keeping conversation');
    }
  }, [isSessionExpired, clearConversation]);

  return {
    conversation,
    isLoading,
    error,
    sendMessage,
    updatePreferences,
    clearConversation,
    isSessionExpired: isSessionExpired(),
    // New persistent features
    persistedPreferences,
    savePreferences,
    isProcessing,
    startAIProcessing,
    saveProcessingState
  };
};
